import { Grid, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import LastXDaysGoalImg from "assets/last-x-days-goal.png";
import OverallGoalImg from "assets/overall-goal.png";
import StreakGoalImg from "assets/streak-goal.png";
import { FormSection } from "components/FormControls/FormSection";
import { HelpPanel } from "components/HelpPanel/HelpPanel";
import { ReactComponent as LayersTwo02 } from "components/Icons/layers-two-02.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import { DefaultLanguageDataState } from "reduxState/translations/translationsSlice";
import {
  AchievementAggregation,
  AchievementCompleteRulePeriod,
  AchievementRuleTypes,
  AchievementTriggers,
  FEAchievementRule,
} from "types/achievements";
import { Menu } from "./Menu/Menu";
import { Rule } from "./Rule";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    section: {
      display: "flex",
      flexDirection: "column",
      padding: theme.spacing(8, 12),
      borderBottom: `1px solid ${theme.gray200}`,
      boxShadow: `0px 1px 1px 0px ${theme.gray100}`,
      backgroundColor: `${theme.gray50}33`,
    },
    rulesSection: {
      padding: theme.spacing(12),
    },
    menuContainer: {
      backgroundColor: theme.gray50,
      borderRadius: "6px",
      border: `1px solid ${theme.gray200}`,
      paddingRight: theme.spacing(10),
    },
    helpPanel: {
      fontSize: 14,
    },
    bolded: {
      fontWeight: 700,
    },
  }),
);

interface Props {
  rules: FEAchievementRule[];
  isEditMode: boolean;
  availableLanguages: DefaultLanguageDataState[];
  errors: any;
}

const GoalTypeHelpPanel = ({
  title,
  description,
  img,
}: {
  title: string;
  description: string;
  img: React.ReactElement;
}) => {
  const classes = useStyles();
  const { message } = useMessage();

  return (
    <HelpPanel
      title={title}
      description={
        <>
          <Typography>
            {message(
              { id: description },
              {
                span: (chunks: React.ReactNode[]) => (
                  <span className={classes.bolded}>{chunks}</span>
                ),
              },
            )}
          </Typography>
          <Typography mt={12} mb={8}>
            {message({ id: "example" })}
          </Typography>
          {img}
        </>
      }
    />
  );
};

export const Rules = ({
  rules,
  errors,
  isEditMode,
  availableLanguages,
}: Props) => {
  const [activeRule, setActiveRule] = useState(0);
  const [helpPanel, setHelpPanel] = useState<string | false>("title");

  const classes = useStyles();
  const { message } = useMessage();

  const { REFERRAL } = AchievementRuleTypes;
  const { TRANSACTION, CUSTOM_EVENT } = AchievementTriggers;
  const { QUANTITY, EXPRESSION } = AchievementAggregation;
  const { OVERALL, LAST_DAY, STREAK } = AchievementCompleteRulePeriod;

  return (
    <FieldArray
      name="rules"
      render={({ push, remove, form }) => {
        const { setFieldValue } = form;
        const ruleErrors = errors && errors.rules;
        const ruleData = rules[activeRule];

        const completeRuleData = ruleData.completeRule.isEditing
          ? ruleData.completeRule?.draft
          : ruleData.completeRule;
        return (
          <Grid container spacing={12}>
            <Grid item xs={8}>
              <FormSection noPadding>
                <section className={classes.section}>
                  <div className={classes.menuContainer}>
                    <Menu
                      activeRule={activeRule}
                      setActiveRule={setActiveRule}
                      rulesCount={rules.length}
                      push={push}
                      remove={remove}
                      errors={
                        ruleErrors
                          ? (ruleErrors as { [key: number]: string })
                          : {}
                      }
                      isEditMode={isEditMode}
                      rules={rules}
                    />
                  </div>
                </section>
                <section className={classes.rulesSection}>
                  {ruleData && (
                    <Rule
                      ruleData={ruleData}
                      activeRule={activeRule}
                      setFieldValue={setFieldValue}
                      availableLanguages={availableLanguages}
                      isEditMode={isEditMode}
                      error={
                        ruleErrors
                          ? (ruleErrors as { [key: number]: string })[
                              activeRule
                            ]
                          : {}
                      }
                      setHelpPanel={setHelpPanel}
                    />
                  )}
                </section>
              </FormSection>
            </Grid>
            <Grid item xs={4}>
              {(!Boolean(helpPanel) || helpPanel === "title") && (
                <HelpPanel
                  title="rulesInAchievement"
                  description="ruleTitleAndDescriptionHelpInfo"
                  links={[
                    {
                      label: message({ id: "documentation" }),
                      path: "https://help.openloyalty.io/main-features/achievements",
                    },
                  ]}
                />
              )}
              {helpPanel === "type-trigger" && (
                <>
                  <HelpPanel title="type" description="ruleTypeHelpInfo" />
                  <HelpPanel
                    title="trigger"
                    description="ruleTriggerHelpInfo"
                    links={[
                      {
                        label: message({ id: "transactions" }),
                        path: "https://help.openloyalty.io/main-features/members/transactions",
                      },
                      {
                        label: message({ id: "customEvents" }),
                        path: "https://help.openloyalty.io/main-features/members/custom-events",
                      },
                    ]}
                  />
                </>
              )}
              {helpPanel === "progress-tracking" && (
                <>
                  <HelpPanel
                    title="example"
                    description="ruleProgressTrackingExampleHelpInfo"
                  />
                  {ruleData.aggregation.type === EXPRESSION && (
                    <HelpPanel
                      title="valueOfEventAttributes"
                      warnBoxText="ruleProgressTrackingWarnBox"
                      links={[
                        {
                          label: message({ id: "customEventSchemas" }),
                          path: "https://help.openloyalty.io/main-features/members/custom-events/custom-event-schemas",
                        },
                      ]}
                    />
                  )}
                </>
              )}
              {helpPanel === "goals" && (
                <>
                  <HelpPanel
                    title="goal"
                    description="ruleGoalHelpInfo"
                    links={[
                      {
                        label: message({ id: "templates" }),
                        path: "https://help.openloyalty.io/main-features/achievements/creating-achievement#use-a-template",
                        icon: <LayersTwo02 />,
                      },
                      {
                        label: message({ id: "documentation" }),
                        path: "https://help.openloyalty.io/main-features/achievements",
                      },
                    ]}
                  />
                  {ruleData.type === REFERRAL &&
                    ruleData.trigger === TRANSACTION &&
                    ruleData.aggregation.type === QUANTITY && (
                      <HelpPanel
                        title="refereeRuleTypeAndGoal"
                        description="ruleGoalToggleHelpInfo"
                        links={[
                          {
                            label: message({ id: "documentation" }),
                            path: "https://help.openloyalty.io/main-features/achievements",
                          },
                        ]}
                      />
                    )}
                  {ruleData.trigger === CUSTOM_EVENT &&
                    //Show help panel if "Count only unique attributes of custom events" toggle is set to true
                    //for submitted values
                    ((ruleData.completeRule.onlyUniqueAttributeValues &&
                      !ruleData.completeRule?.isEditing) ||
                      //​​or draft
                      ruleData.completeRule.draft
                        ?.onlyUniqueAttributeValues) && (
                      <HelpPanel
                        title="uniqueCustomEventAttributes"
                        description="uniqueCustomEventAttributesHelpPanel"
                        links={[
                          {
                            label: message({ id: "documentation" }),
                            path: "https://help.openloyalty.io/main-features/achievements",
                          },
                          {
                            label: message({ id: "customEventSchemas" }),
                            path: "https://help.openloyalty.io/main-features/members/custom-events/custom-event-schemas",
                          },
                        ]}
                      />
                    )}
                  {completeRuleData?.period?.type === OVERALL && (
                    <GoalTypeHelpPanel
                      title="overall"
                      description="overallGoalHelpInfo"
                      img={<img src={OverallGoalImg} alt="overall" />}
                    />
                  )}
                  {completeRuleData?.period?.type === LAST_DAY && (
                    <GoalTypeHelpPanel
                      title="lastXDays"
                      description="lastxDaysHelpInfo"
                      img={<img src={LastXDaysGoalImg} alt="last-x-days" />}
                    />
                  )}
                  {completeRuleData?.period?.type === STREAK && (
                    <GoalTypeHelpPanel
                      title="streak"
                      description="streakHelpInfo"
                      img={<img src={StreakGoalImg} alt="streak" />}
                    />
                  )}
                </>
              )}
              {helpPanel === "conditions" && (
                <HelpPanel
                  title="triggerConditions"
                  description="ruleConditionsHelpInfo"
                  links={[
                    {
                      label: message({ id: "expression" }),
                      path: "https://help.openloyalty.io/main-features/expressions",
                    },
                  ]}
                />
              )}
              {helpPanel === "event-limit" && (
                <HelpPanel
                  title="eventFrequencyLimit"
                  description="ruleLimitHelpInfo"
                  warnBoxText="ruleLimitWarnBox"
                />
              )}
            </Grid>
          </Grid>
        );
      }}
    />
  );
};
